import { useState } from 'react';
import type { BookFilterState } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  filters: BookFilterState;
  onChange: (next: BookFilterState) => void;
}

export function BookAdvancedFilters({ filters, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const parseNum = (v: string): number | undefined =>
    v === '' ? undefined : Number(v);

  return (
    <div className="border rounded p-3">
      <button
        type="button"
        className="text-sm font-medium text-primary-600 flex items-center gap-1"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span aria-hidden="true">{open ? '▼' : '▶'}</span>
        {t('App.Admin.Books.BookAdvancedFilters.Title')}
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <label className="flex flex-col text-sm">
            <span className="text-neutral-700">{t('App.Admin.Books.BookAdvancedFilters.RendererTypeLabel')}</span>
            <select
              value={filters.rendererType ?? ''}
              onChange={e => onChange({
                ...filters,
                rendererType: parseNum(e.target.value) as BookFilterState['rendererType'],
                page: 1,
              })}
              className="border rounded px-3 py-2 min-h-[44px]"
            >
              <option value="">{t('App.Admin.Books.BookAdvancedFilters.PlaceholderAny')}</option>
              <option value="1">{t('App.Common.RendererType.CustomHtml')}</option>
              <option value="2">{t('App.Common.RendererType.EpubJs')}</option>
              <option value="3">{t('App.Common.RendererType.Pdf')}</option>
            </select>
          </label>

          <label className="flex flex-col text-sm">
            <span className="text-neutral-700">{t('App.Admin.Books.BookAdvancedFilters.ComplexityMinLabel', { value: filters.complexityMin ?? 0 })}</span>
            <input
              type="number" min={0} step={1}
              value={filters.complexityMin ?? ''}
              onChange={e => onChange({
                ...filters,
                complexityMin: parseNum(e.target.value),
                page: 1,
              })}
              className="border rounded px-3 py-2 min-h-[44px]"
              placeholder={t('App.Admin.Books.BookAdvancedFilters.PlaceholderAny')}
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="text-neutral-700">{t('App.Admin.Books.BookAdvancedFilters.ComplexityMaxLabel', { value: filters.complexityMax ?? '-' })}</span>
            <input
              type="number" min={0} step={1}
              value={filters.complexityMax ?? ''}
              onChange={e => onChange({
                ...filters,
                complexityMax: parseNum(e.target.value),
                page: 1,
              })}
              className="border rounded px-3 py-2 min-h-[44px]"
              placeholder={t('App.Admin.Books.BookAdvancedFilters.PlaceholderAny')}
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="text-neutral-700">{t('App.Admin.Books.BookAdvancedFilters.CreatedAfterLabel')}</span>
            <input
              type="date"
              value={filters.createdAfter?.slice(0, 10) ?? ''}
              onChange={e => onChange({
                ...filters,
                createdAfter: e.target.value || undefined,
                page: 1,
              })}
              className="border rounded px-3 py-2 min-h-[44px]"
            />
          </label>

          <label className="flex flex-col text-sm">
            <span className="text-neutral-700">{t('App.Admin.Books.BookAdvancedFilters.CreatedBeforeLabel')}</span>
            <input
              type="date"
              value={filters.createdBefore?.slice(0, 10) ?? ''}
              onChange={e => onChange({
                ...filters,
                createdBefore: e.target.value || undefined,
                page: 1,
              })}
              className="border rounded px-3 py-2 min-h-[44px]"
            />
          </label>

          <label className="flex items-center gap-2 text-sm mt-6">
            <input
              type="checkbox"
              checked={filters.hasCover === true}
              onChange={e => onChange({
                ...filters,
                hasCover: e.target.checked ? true : undefined,
                page: 1,
              })}
            />
            {t('App.Admin.Books.BookAdvancedFilters.HasCoverImage')}
          </label>

          <label className="flex items-center gap-2 text-sm mt-6 md:col-span-3">
            <input
              type="checkbox"
              checked={filters.includeDeleted === true}
              onChange={e => onChange({
                ...filters,
                includeDeleted: e.target.checked ? true : undefined,
                page: 1,
              })}
            />
            {t('App.Admin.Books.BookAdvancedFilters.ShowDeleted')}
          </label>

          <label className="flex items-center gap-2 text-sm md:col-span-3">
            <input
              type="checkbox"
              checked={filters.includeArchived === true}
              onChange={e => onChange({
                ...filters,
                includeArchived: e.target.checked ? true : undefined,
                page: 1,
              })}
            />
            {t('App.Admin.Books.BookAdvancedFilters.ShowArchived')}
          </label>
        </div>
      )}
    </div>
  );
}
